export const contactDetails = {
  email: "info@ignitumsolutions.com",
  linkedin: "https://www.linkedin.com/in/ignitumsolutions/",
};

export const founderDetails = {
  name: "Ryan Carlisle",
  title: "Founder, Inventor, and Technology Advocate",
};

const founderBadges = [
  "Founder and inventor",
  "Software and systems architecture",
  "Applied AI implementation",
  "Technology advocate and technical contributor",
  "Prototype-to-production work",
];

const scePath = "/synthetic-cognition-engine";
const productNavigationItems = [
  { label: "Synthetic Cognition Engine", to: scePath },
];

export const siteMeta = {
  siteName: "Ignitum Solutions",
  siteUrl: "https://www.ignitumsolutions.com",
  defaultImage: "/apple-icon.png",
  defaultTitle:
    "Ignitum Solutions | Founder-Led Engineering, Invention, and Trustworthy AI Systems",
  defaultDescription:
    "Founder-led engineering and invention for advanced software systems, applied and agentic AI, trustworthy decision systems, and difficult technical work.",
  themeColor: "#081018",
};

export const navigationItems = [
  { label: "Home", to: "/" },
  { label: "Approach", to: "/approach" },
  { label: "Focus Areas", to: "/focus-areas" },
  {
    label: "Products",
    children: productNavigationItems,
  },
  { label: "Contact", to: "/contact", cta: true },
];

export const footerLinks = [
  { label: "Home", to: "/" },
  { label: "Approach", to: "/approach" },
  { label: "Focus Areas", to: "/focus-areas" },
  ...productNavigationItems,
  { label: "Current Work", to: "/current-work" },
];

export const footerContent = {
  blurb:
    "Founder-led engineering and invention across advanced software, applied and agentic AI, complex architecture, and trustworthy decision systems.",
  note: "",
};

export const homePage = {
  meta: {
    title: siteMeta.defaultTitle,
    description: siteMeta.defaultDescription,
    path: "/",
  },
  splash: {
    tagline: "Software systems, AI implementations, and decision tools.",
    actionLabel: "See what Ignitum does",
  },
  hero: {
    eyebrow: "Founder-led engineering and invention",
    title:
      "Engineering software, AI, and decision systems that can be trusted in production.",
    description:
      "Ignitum builds custom software platforms, internal tools, AI-backed and agentic applications, and decision-support systems. That can mean web, desktop, mobile, or edge deployments, along with the frameworks those systems depend on. The common thread is harder implementation work where off-the-shelf software is not enough.",
    actions: [
      { label: "Start a conversation", to: "/contact", variant: "primary" },
      {
        label: "Explore focus areas",
        to: "/focus-areas",
        variant: "secondary",
      },
    ],
    signalTitle: "What Ignitum builds",
    signals: [
      "Web, desktop, mobile, edge, and internal software systems",
      "AI-backed and agentic workflows with the frameworks around them",
      "Flagship internal work on bounded, explainable decision systems",
    ],
    signalNote:
      "Not a generic website shop. Not a one-product company either.",
  },
  focusAreas: {
    eyebrow: "Core work",
    title: "Software systems, applied AI, and decision tooling.",
    description:
      "The work can take the form of web software, internal systems, AI-backed products, decision-support tools, or invention-led prototypes. The common thread is harder implementation work, not a fixed format.",
    items: [
      {
        label: "Software platforms",
        title: "Custom software systems.",
        description:
          "Web, desktop, mobile, edge, and internal systems where architecture, data flow, and system behavior matter more than surface polish.",
        points: ["Web and internal tools", "System behavior", "Real operations"],
      },
      {
        label: "Applied AI",
        title: "Applied AI and agentic systems.",
        description:
          "AI features, agentic workflows, and the supporting frameworks that make them usable inside real products and operating software.",
        points: ["Agents and workflows", "Frameworks and tooling", "Usable software"],
      },
      {
        label: "Decision systems",
        title: "Decision and control tooling.",
        description:
          "Systems where actions or recommendations need clearer bounds, review, traceability, and operational fit.",
        points: ["Bounded behavior", "Clearer reasoning", "Operational use"],
      },
      {
        label: "Validation",
        title: "Prototype and invention work.",
        description:
          "New technical directions shaped into testable prototypes and production paths rather than open-ended experimentation.",
        points: ["Hard-question prototyping", "Technical proof", "Path to production"],
      },
    ],
  },
  currentDirection: {
    eyebrow: "Current work",
    title: "External engineering work and internal engine development.",
    description:
      "Ignitum is active in custom software and applied AI implementation work while continuing internal development of the Synthetic Cognition Engine.",
    activeNow: {
      title: "Client and implementation work",
      items: [
        "Custom software across web, desktop, mobile, edge, and internal system contexts",
        "Applied AI and agentic workflows built into usable products and operating software",
        "Architecture and technical problem-solving for non-routine environments",
      ],
    },
    inProgress: {
      title: "Internal direction",
      items: [
        "The Synthetic Cognition Engine and related decision-system work remain under active development",
        "Early implementation work is in progress, with public detail kept intentionally high level",
        "Stronger proof material should come from working systems rather than from heavy pre-release marketing",
      ],
    },
    actions: [],
  },
  founder: {
    eyebrow: "Founder",
    title: "Founder-led by Ryan Carlisle",
    description:
      "Ryan Carlisle is the founder, inventor, and hands-on systems architect behind Ignitum.",
    body: [
      "His work spans software architecture, applied AI, and invention-led implementation, especially where the problem is too custom, messy, or high-consequence for commodity tools.",
      "He stays close to the system itself: shaping the architecture, pressure-testing the hard parts, and keeping the claims anchored to what is real.",
    ],
    badges: founderBadges,
    actions: [],
  },
  conversation: {
    eyebrow: "Contact",
    title: "Start a serious technical conversation",
    description:
      "For difficult software or AI work, architecture questions, invention-led prototypes, or early discussions around decision systems, start with a direct note.",
    actions: [
      {
        label: "Email Ignitum",
        to: `mailto:${contactDetails.email}`,
        variant: "primary",
        external: true,
      },
      { label: "Contact page", to: "/contact", variant: "secondary" },
    ],
  },
};

export const approachPage = {
  meta: {
    title: "Approach | Ignitum Solutions",
    description:
      "Ignitum's approach combines invention, software architecture, bounded behavior, and production-minded engineering for difficult technical work.",
    path: "/approach",
  },
  hero: {
    eyebrow: "Approach",
    title: "Invention with engineering discipline.",
    description:
      "Ignitum works in problem spaces where standard products, standard delivery patterns, or standard assumptions do not hold. That does not lower the bar. Novel systems still need boundaries, validation, and a credible path from prototype to operation.",
    signals: [
      "Software and systems work shaped around real operating conditions",
      "Clarity and bounded behavior where outcomes matter",
      "Prototype-to-production thinking from the start",
    ],
  },
  opening: [
    "A large share of software and systems marketing mistakes novelty for readiness. It celebrates possibility without accounting for constraints, failure modes, integrations, or operating conditions. That is where serious projects get into trouble.",
    "Ignitum takes the opposite view. Difficult work still needs architecture, implementation discipline, and honest statements about what exists today versus what is still exploratory.",
  ],
  principles: [
    {
      label: "Boundaries",
      title: "Novel work still needs boundaries.",
      description:
        "A system can be inventive without being open-ended. Limits, controls, and assumptions have to be explicit early.",
    },
    {
      label: "Decision quality",
      title: "Important system behavior cannot stay opaque by default.",
      description:
        "If a system recommends, routes, coordinates, or decides, there should be a usable path to understand how it behaves and how it fails.",
    },
    {
      label: "Prototype-to-production",
      title: "Prototype with a real destination.",
      description:
        "Exploration should answer technical questions and improve the path to deployment instead of turning into permanent demo theater.",
    },
    {
      label: "Honest readiness",
      title: "Say clearly what is mature and what is not.",
      description:
        "Novel technical work loses credibility when experimentation is marketed as finished capability. Ignitum avoids that trap on purpose.",
    },
  ],
  relevance: {
    title: "Where this approach matters most",
    description:
      "Ignitum tends to matter when the work is novel, technically awkward, high-consequence, or spread across multiple parts of the stack.",
    items: [
      {
        label: "01",
        title: "Standard products cannot carry the full load",
        description:
          "Existing products may solve part of the problem while missing the real constraints that make the environment difficult.",
      },
      {
        label: "02",
        title: "The system cannot be treated as a black box",
        description:
          "Decision-support, automation, and agentic work often need bounded behavior, explainability, or controlled failure handling to be usable.",
      },
      {
        label: "03",
        title: "A serious prototype is needed before commitment",
        description:
          "Some ideas need disciplined validation and architecture before a team can sensibly decide whether to invest further.",
      },
    ],
  },
  comparison: {
    title: "How invention, R&D, and deployable systems are separated",
    description:
      "Exploration is useful when it answers technical questions and de-risks the next implementation step. It becomes a problem when it is presented as finished capability.",
    items: [
      {
        title: "R&D and invention work",
        rows: [
          {
            label: "Purpose",
            text: "Test feasibility, sharpen interfaces, and expose technical uncertainty before a team commits to the wrong structure.",
          },
          {
            label: "Output",
            text: "Prototype behavior, evaluation patterns, and technical evidence about what should or should not move forward.",
          },
          {
            label: "Standard",
            text: "Useful if it reduces ambiguity and creates better engineering decisions rather than just a stronger presentation.",
          },
        ],
      },
      {
        title: "Deployable systems",
        rows: [
          {
            label: "Purpose",
            text: "Support real work with explicit boundaries, ownership, controls, and operational reliability.",
          },
          {
            label: "Output",
            text: "Software that can be tested, observed, maintained, and used without hand-waving around its limitations.",
          },
          {
            label: "Standard",
            text: "Ready only when behavior, integration, and operating assumptions are explicit enough to trust in the real environment.",
          },
        ],
      },
    ],
  },
  process: {
    title: "How work is typically shaped",
    description:
      "The sequence varies by problem, but the pattern is consistent: define the operating reality, build the smallest useful implementation, and validate behavior before widening scope.",
    steps: [
      {
        title: "Frame the environment",
        description:
          "Clarify system boundaries, decision context, data dependencies, integration surfaces, and the failure conditions that actually matter.",
      },
      {
        title: "Build the useful core",
        description:
          "Create the smallest implementation that exercises the critical path instead of diluting effort across generic feature lists.",
      },
      {
        title: "Validate and refine",
        description:
          "Use testing, evaluation, and operational review to decide what should harden, what should change, and what should be discarded.",
      },
    ],
  },
};

export const focusAreasPage = {
  meta: {
    title: "Focus Areas | Ignitum Solutions",
    description:
      "Focused work across software systems, applied and agentic systems, complex architecture, trustworthy decision systems, and invention-led technical validation.",
    path: "/focus-areas",
  },
  hero: {
    eyebrow: "Focus areas",
    title: "Difficult systems, not generic service lines.",
    description:
      "Ignitum works across advanced software delivery, applied and agentic systems, system architecture, and invention-led prototyping. That can include web, desktop, mobile, and edge software, along with the frameworks, interfaces, and controls that make those systems usable in practice. The scope is broader than one internal engine, but narrower than a generic services shop.",
    signals: [
      "Web, desktop, mobile, edge, and internal software systems",
      "Agentic implementations and the software around them",
      "Flagship internal IP as one direction, not the whole company",
    ],
  },
  areas: [
    {
      label: "01",
      title: "Trustworthy Decision Systems",
      rows: [
        {
          label: "What this means",
          text: "Designing decision-support or decision-making systems for contexts where fully opaque behavior is not enough.",
        },
        {
          label: "Where it matters",
          text: "Operational environments that need bounded behavior, clearer reasoning, auditability, or controllable failure modes.",
        },
        {
          label: "How it is approached",
          text: "Shape decision logic, evaluation, and controls around the real environment instead of treating explainability as an add-on.",
        },
      ],
    },
    {
      label: "02",
      title: "Applied and Agentic System Implementations",
      rows: [
        {
          label: "What this means",
          text: "Integrating model-driven or agentic capability into usable software, with the orchestration, interfaces, evaluation, and fallback needed to make it durable.",
        },
        {
          label: "Where it matters",
          text: "Operator tools, workflow software, internal platforms, and customer-facing products that need more than a demo to be useful.",
        },
        {
          label: "How it is approached",
          text: "Treat models and agents as components inside a wider system that includes interfaces, evaluation, observability, and operational fallback.",
        },
      ],
    },
    {
      label: "03",
      title: "Architecture for Complex Systems",
      rows: [
        {
          label: "What this means",
          text: "Structuring software that spans multiple services, integrations, or unusual constraints without losing clarity.",
        },
        {
          label: "Where it matters",
          text: "Distributed systems, multi-service applications, web or edge deployments, and builds that carry non-trivial reliability, governance, or integration complexity.",
        },
        {
          label: "How it is approached",
          text: "Reduce ambiguity early, define boundaries clearly, and make failure handling an explicit design concern rather than a late patch.",
        },
      ],
    },
    {
      label: "04",
      title: "Invention, Prototyping, and Technical Validation",
      rows: [
        {
          label: "What this means",
          text: "Targeted exploratory work to validate technical direction, de-risk new behavior, and create artifacts that can inform production strategy.",
        },
        {
          label: "Where it matters",
          text: "Novel or ambiguous problem spaces where the right architecture is not obvious and standard products do not fit cleanly.",
        },
        {
          label: "How it is approached",
          text: "Prototype with intent, measure what matters, and keep only the technical paths that survive scrutiny.",
        },
      ],
    },
  ],
  breadth: {
    title: "Broader than one internal engine",
    description:
      "SCE is a flagship internal direction, but Ignitum is built around a wider capability set for difficult custom work across software delivery, architecture, and applied systems.",
    items: [
      {
        label: "01",
        title: "Software across web, desktop, mobile, and edge",
        description:
          "The work is not limited to one form factor. It can include operator tools, internal systems, customer-facing products, and edge-connected software where the system as a whole is the hard part.",
      },
      {
        label: "02",
        title: "Agentic systems and consuming frameworks",
        description:
          "Ignitum can build agentic workflows and the surrounding frameworks, interfaces, and controls required to put those systems to work responsibly.",
      },
      {
        label: "03",
        title: "Architecture for non-standard environments",
        description:
          "Useful where unusual constraints, integrations, reliability demands, or governance concerns make standard patterns insufficient.",
      },
      {
        label: "04",
        title: "Invention and internal IP",
        description:
          "Flagship internal engine work matters, but it sits inside a broader engineering capability rather than defining the entire company.",
      },
    ],
  },
};

export const scePage = {
  meta: {
    title: "Synthetic Cognition Engine | Ignitum Solutions",
    description:
      "Synthetic Cognition Engine is Ignitum's domain-agnostic internal engine effort focused on bounded, explainable decision quality under uncertainty. It is under active development, with early production implementations in progress.",
    path: scePath,
  },
  hero: {
    eyebrow: "Flagship internal direction",
    title: "Synthetic Cognition Engine",
    description:
      "Ignitum's Synthetic Cognition Engine is a domain-agnostic internal engine effort focused on bounded, explainable, and operationally relevant decision behavior under real-world uncertainty.",
    supportLine: "Early production implementations are now in progress.",
    actions: [{ label: "Discuss the Direction", to: "/contact", variant: "primary" }],
    status: ["Under Development", "Patent Pending"],
    panelLabel: "Core themes",
    panelNote:
      "Public detail remains intentionally limited while active engineering continues.",
    highlights: [
      {
        title: "Bounded behavior",
        text: "Control and constraint where open-ended output is not enough.",
      },
      {
        title: "Explainable flow",
        text: "Behavior intended to remain reviewable and auditable.",
      },
      {
        title: "Safety-aware operation",
        text: "Shaped for environments where poor decisions carry consequences.",
      },
      {
        title: "Production-minded engineering",
        text: "Built as a real implementation program, not research theater.",
      },
    ],
    signalNodes: ["Signal", "Evaluation", "Action"],
    panelTitle:
      "A serious engine direction, framed at the right level of public detail.",
  },
  why: {
    eyebrow: "Why this exists",
    title: "Why SCE exists",
    description:
      "Many systems can produce outputs. Far fewer are built to support controlled decisions under uncertainty, within real operational constraints, and in a form that can still be examined afterward.",
    body: [
      "Much of the public discussion around model-driven systems is centered on output generation, novelty, or open-ended capability. That becomes less useful when timing, safety, traceability, and controllable behavior matter to the real environment.",
      "SCE exists to explore that gap seriously: decision quality that can be bounded, examined, and engineered for operational use rather than treated as an opaque result that must simply be accepted.",
    ],
    signals: [
      "Uncertainty without enough traceability is a problem, not a feature.",
      "Real environments need decisions that can survive constraints, timing pressure, and review.",
      "For some systems, output quality alone is not the right standard. Decision quality is.",
    ],
  },
  differentiators: {
    eyebrow: "What makes this direction different",
    title: "Built for decisions that can be reviewed",
    description:
      "The goal is not a broader capability claim. It is a more disciplined engine direction for environments where behavior has to be bounded, explainable, and fit for use.",
    items: [
      {
        label: "01",
        title: "Bounded behavior",
        description:
          "SCE is being shaped around controlled behavior under constraints rather than open-ended output for its own sake.",
      },
      {
        label: "02",
        title: "Explainable and auditable flow",
        description:
          "The direction centers on decision paths that can be examined, challenged, and improved rather than treated as magic.",
      },
      {
        label: "03",
        title: "Safety-aware operational thinking",
        description:
          "The work is aimed at environments where poor decisions carry operational consequences and failure handling cannot be an afterthought.",
      },
      {
        label: "04",
        title: "Production-minded engineering",
        description:
          "SCE is being pursued as a serious invention and engineering program, not as a research narrative detached from implementation reality.",
      },
    ],
  },
  applicability: {
    eyebrow: "Where it may matter",
    title: "Shaped to be domain agnostic",
    description:
      "SCE is being developed as a broader engine direction rather than a single-purpose feature. That makes it relevant to very different environments where uncertainty, timing, control, and traceability matter.",
    items: [
      {
        label: "01",
        title: "Simulation and training environments",
        description:
          "Useful where decision behavior needs to be tested, observed, and refined before it is trusted closer to operations.",
      },
      {
        label: "02",
        title: "Robotics and embodied systems",
        description:
          "Relevant where systems need controllable decision quality under changing conditions rather than purely abstract model output.",
      },
      {
        label: "03",
        title: "Industrial and infrastructure operations",
        description:
          "Potentially useful in environments where timing, safety, and traceability matter more than surface-level automation claims.",
      },
      {
        label: "04",
        title: "Operational software and decision support",
        description:
          "Applicable to software environments where recommendations or actions have to be understood, bounded, and fit for real-world use.",
      },
    ],
  },
  currentState: {
    eyebrow: "What is real today",
    title: "Active engineering, with public detail kept disciplined",
    description:
      "SCE is an active engineering effort inside Ignitum. The work is ongoing, early production implementations are in progress, and public detail remains intentionally restrained while the engine matures.",
    activeNow: {
      title: "Active now",
      items: [
        "Engineering and validation work around a domain-agnostic internal engine direction for bounded decision systems",
        "Production-minded implementation thinking rather than vague experimentation detached from use",
        "Early production implementations being pursued in parallel with continued invention and refinement",
      ],
    },
    heldBack: {
      title: "Still intentionally high level",
      items: [
        "Detailed mechanics, formulas, schemas, and internal decision structures remain private while development continues",
        "Public description is limited to direction, operating goals, and honest statements about readiness",
        "Stronger proof material should come from implementation and validation, not from premature disclosure",
      ],
    },
  },
  closing: {
    eyebrow: "Contact",
    title: "Interested in the direction?",
    description:
      "For serious conversations around high-consequence decision systems, early implementation fit, or future collaboration, start with a direct note.",
    actions: [{ label: "Start a Conversation", to: "/contact", variant: "primary" }],
  },
};

export const currentWorkPage = {
  meta: {
    title: "Current Work | Ignitum Solutions",
    description:
      "Active engineering, applied and agentic AI, and internal invention work across complex systems and trustworthy decision architecture.",
    path: "/current-work",
  },
  hero: {
    eyebrow: "Current work",
    title: "What exists today, and what is still being shaped.",
    description:
      "Ignitum is active today in founder-led engineering work across software delivery, architecture, applied and agentic AI implementation, and disciplined technical exploration. Internal flagship invention around trustworthy decision systems is progressing in parallel, but it is still described as development rather than product readiness.",
    signals: [
      "Applied engineering work today",
      "Disciplined prototype and validation work",
      "Flagship internal direction without false readiness claims",
    ],
  },
  items: [
    {
      label: "01",
      title: "Applied engineering work",
      description:
        "Current external work includes architecture, implementation, and technical problem-solving across web, desktop, mobile, edge, and internal software environments.",
    },
    {
      label: "02",
      title: "Technical validation and prototyping",
      description:
        "Ignitum also runs targeted prototype work for agentic systems, model-backed products, and difficult new ideas that need to be pressure-tested before they harden into delivery.",
    },
    {
      label: "03",
      title: "Flagship internal invention direction",
      description:
        "SCE and related trustworthy decision-system work remain serious internal priorities, but they are still being validated and are not represented as finished commercial offerings.",
    },
  ],
  discussion: {
    title: "What can be discussed now",
    rows: [
      {
        label: "Available today",
        text: "High-level architecture direction, implementation priorities, collaboration fit, and the technical reasoning behind current work.",
      },
      {
        label: "Handled carefully",
        text: "Sensitive implementation detail, immature prototype material, and anything that would overstate readiness or reveal work before it is defensible.",
      },
    ],
  },
};

export const contactPage = {
  meta: {
    title: "Contact | Ignitum Solutions",
    description:
      "Contact Ignitum Solutions for founder-led engineering, invention, complex architecture, and serious applied or agentic AI work.",
    path: "/contact",
  },
  hero: {
    eyebrow: "Contact",
    title: "Start a technical conversation.",
    description:
      "Ignitum is open to serious discussions around difficult software work, applied or agentic AI systems, architecture for complex environments, invention-led prototyping, and early conversations where decision quality or bounded behavior matter.",
    signals: [
      "Difficult implementation work",
      "Complex architecture and invention",
      "Early conversations with real technical depth",
    ],
  },
  goodFit: [
    "A difficult software or AI problem needs architecture plus hands-on implementation",
    "A system is too important to leave fully opaque",
    "A novel technical idea needs disciplined prototyping and validation",
    "An existing stack or off-the-shelf product is not enough for the environment",
  ],
  response: [
    {
      label: "Email",
      text: contactDetails.email,
      href: `mailto:${contactDetails.email}`,
    },
    {
      label: "LinkedIn",
      text: "Ignitum Solutions",
      href: contactDetails.linkedin,
    },
  ],
  note:
    "If the conversation is specific and technical, include a brief summary of the problem, the current constraints, and what you are trying to validate or build.",
};

export function buildStructuredData({ pageName, description, path }) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: siteMeta.siteName,
        url: siteMeta.siteUrl,
        logo: `${siteMeta.siteUrl}${siteMeta.defaultImage}`,
        email: contactDetails.email,
        sameAs: [contactDetails.linkedin],
        founder: {
          "@type": "Person",
          name: founderDetails.name,
        },
        description: siteMeta.defaultDescription,
      },
      {
        "@type": "WebSite",
        name: siteMeta.siteName,
        url: siteMeta.siteUrl,
      },
      {
        "@type": "WebPage",
        name: pageName,
        description,
        url: `${siteMeta.siteUrl}${path}`,
        isPartOf: {
          "@type": "WebSite",
          name: siteMeta.siteName,
          url: siteMeta.siteUrl,
        },
        about: {
          "@type": "Organization",
          name: siteMeta.siteName,
        },
      },
    ],
  };
}
